import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Keeps the mobile soft keyboard from panning/scrolling the page and instead
 * resizes the layout to fit the visible area, like a native app. Scoped to the
 * chat routes ("/" and "/c/...") only.
 *
 * Two mechanisms are used:
 *
 * 1. Native: toggles `interactive-widget=resizes-content` on the viewport meta
 *    tag. Supported by Chromium 108+ on Android, Firefox 133+, and Samsung
 *    Internet 21+. It makes the browser resize the layout viewport (and the
 *    viewport units derived from it, like `100dvh`) when the keyboard opens
 *    instead of overlaying it and panning the page.
 *
 * 2. iOS fallback: Safari/WebKit do not support the meta key (yet), so we
 *    track `window.visualViewport.height` while the keyboard is open and
 *    publish it as `--chat-viewport-height` on <html>. The rule
 *    `html.chat-keyboard-resize .app-shell` in styles.css then sizes the app
 *    shell to that height, keeping the composer above the keyboard and the
 *    header on screen.
 */

const VIEWPORT_META = 'meta[name="viewport"]';
const RESIZE_CONTENT = "interactive-widget=resizes-content";

// Cached so we can restore the exact original content when leaving the chat
// page, even if it already contained an `interactive-widget` value.
let baseViewportContent: string | null = null;

function viewportMeta(): HTMLMetaElement | null {
  return document.querySelector(VIEWPORT_META);
}

function isChatPage(pathname: string) {
  return pathname === "/" || pathname.startsWith("/c/");
}

function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac desktop browser.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function enableViewportResize() {
  const meta = viewportMeta();
  if (!meta) return;
  if (baseViewportContent === null)
    baseViewportContent = meta.getAttribute("content");
  const base = baseViewportContent ?? "";
  const keys = base.split(",").map((part) => part.trim());
  if (!keys.includes(RESIZE_CONTENT)) {
    meta.setAttribute(
      "content",
      base ? `${base},${RESIZE_CONTENT}` : RESIZE_CONTENT,
    );
  }
}

function disableViewportResize() {
  if (baseViewportContent === null) return;
  const meta = viewportMeta();
  if (meta) meta.setAttribute("content", baseViewportContent);
}

export function useChatKeyboardResize() {
  const { pathname } = useLocation();
  const chat = isChatPage(pathname);

  // Native resize behavior plus the CSS hook the layout sizes against.
  useEffect(() => {
    document.documentElement.classList.toggle("chat-keyboard-resize", chat);
    if (chat) enableViewportResize();
    else disableViewportResize();
    return () => {
      document.documentElement.classList.remove("chat-keyboard-resize");
      if (chat) disableViewportResize();
    };
  }, [chat]);

  // iOS fallback: resize the layout to the visual viewport while the
  // keyboard is open (Safari ignores the meta key above).
  useEffect(() => {
    if (!chat || !isIOS()) return;
    const visualViewport = window.visualViewport;
    const root = document.documentElement;
    const KEY = "--chat-viewport-height";
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!visualViewport) return;
        if (visualViewport.height < window.innerHeight - 24) {
          root.style.setProperty(KEY, `${Math.round(visualViewport.height)}px`);
          // Safari pans the page up to keep the focused input visible; the
          // layout now fits the visible area, so bring the top back.
          if (window.scrollY > 0) window.scrollTo(0, 0);
        } else {
          root.style.removeProperty(KEY);
        }
      });
    };
    update();
    visualViewport?.addEventListener("resize", update);
    visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      root.style.removeProperty(KEY);
      visualViewport?.removeEventListener("resize", update);
      visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [chat]);
}
